import { useEffect } from 'react'

import { useForm, zodResolver } from '@mantine/form'
import { useFocusWithin } from '@mantine/hooks'
import { z } from 'zod'

import type { FundSymbol } from '@repo/common//queries/products/products_names'
import { useGetWalletBalance } from '@repo/common/queries/blockchain/useGetWalletBalance'
import {
  useGetFundPrices,
  useGetMinterDisabled,
  useGetMinterLimitLeft,
} from '@repo/common/queries/funds'
import { useGetGovMintRewards } from '@repo/common/queries/gov'
import { useIsSuccess } from '@repo/common/queries/helpers/utilities'
import { useNextPurchaseLevel } from '@repo/common/queries/token_auth'

const default_reason = 'Enter an amount'

// eslint-disable-next-line max-lines-per-function, complexity
export const useMintFields = ({
  fund_symbol: symbol,
  starting_tokens_in = 1000,
}: {
  fund_symbol: FundSymbol
  starting_tokens_in?: number
}) => {
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      tokens_in: starting_tokens_in,
      tokens_out: 0,
      slippage_percentage: 3,
    } as {
      tokens_in: number
      tokens_out: number
      slippage_percentage: number
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: false,
    validate: (values) => zodResolver(get_schema())(values),
  })
  const { get_isSuccess, combine_isSuccess } = useIsSuccess()

  const { data: fund_prices } = combine_isSuccess(useGetFundPrices({ symbol }))
  const { balance: balance_USDC, isConnected: wallet_isConnected } =
    combine_isSuccess(useGetWalletBalance('USDC'))
  const { balance: balance_fund } = combine_isSuccess(
    useGetWalletBalance(symbol),
  )
  const disabled_query = combine_isSuccess(useGetMinterDisabled({ symbol }))
  const { data: deposit_limit } = combine_isSuccess(
    useGetMinterLimitLeft({ symbol }),
  )

  const { level, allowed_kyc_levels } = combine_isSuccess(
    useNextPurchaseLevel({
      symbol,
      deposit_usdc_tokens: form.getValues().tokens_in ?? 0,
    }),
  )

  const get_schema = () => {
    return z.object({
      tokens_in: z
        .number({ message: default_reason })
        .positive()
        .max(Math.min(balance_USDC, deposit_limit ?? 0), {
          message:
            balance_USDC <= (deposit_limit ?? 0)
              ? 'Not enough USDC balance'
              : 'Above current deposit capacity',
        }),
      tokens_out: z.number({ message: default_reason }).positive(),
    })
  }

  const tokens_in_focus = useFocusWithin()
  const tokens_out_focus = useFocusWithin()

  useEffect(() => {
    if (fund_prices == null || fund_prices.oracle_outdated == true) return

    if (tokens_out_focus.focused) {
      form.setValues({
        tokens_in: form.getValues().tokens_out ?? 0 * fund_prices.mint,
      })
    } else {
      form.setValues({
        tokens_out: (form.getValues().tokens_in ?? 0) / fund_prices.mint,
      })
    }

    // only updated if the fund_price_changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fund_prices?.mint, fund_prices?.oracle_outdated])

  form.watch('tokens_in', ({ value }) => {
    if (!tokens_in_focus.focused) return
    if (fund_prices == null || fund_prices.oracle_outdated == true) return
    if (value == null) return

    form.setValues({
      tokens_out: value / fund_prices.mint,
    })
  })

  form.watch('tokens_out', ({ value }) => {
    if (!tokens_out_focus.focused) return
    if (fund_prices == null || fund_prices.oracle_outdated == true) return
    if (value == null) return

    form.setValues({
      tokens_in: value * fund_prices.mint,
    })
  })

  const tokens_in = form.getValues().tokens_in ?? 0

  const { data: fisn_rewards } = useGetGovMintRewards({
    mint_value: tokens_in == 0 ? 1000 : tokens_in,
    symbol,
  })

  if (tokens_in == 0) {
    fisn_rewards.token_value = 0
    fisn_rewards.tokens = 0
  }

  const is_loading = get_isSuccess() == false
  const form_valid = form.isValid() && !is_loading
  const error_reason =
    disabled_query.reason ||
    get_schema().safeParse(form.getValues()).error?.errors[0]?.message ||
    default_reason

  return {
    form,
    balance_USDC,
    balance_fund,
    wallet_isConnected,
    form_valid,
    paused: disabled_query.disabled,
    is_loading,
    error_reason,
    tokens_in_focus,
    tokens_out_focus,
    fund_price: fund_prices?.mint,
    fisn_rewards,
    level,
    allowed_kyc_levels,
    deposit_limit,
  }
}
